import * as Phaser from "phaser";
import {
  AssetLocator,
  type Animation,
  ANIMATION_EFFECTS,
  DEFAULT_ANIMATION,
} from "./AssetLocator";

/**
 * Visual-only sprite for an animation effect (hammer hit, umbrella while flying, etc.).
 * Keeps logic out of CatSprite. The effect sprite is *not* a Phaser.Container; it is a
 * single Sprite added directly to the Cat container so its depth can be controlled
 * alongside other parts.
 */
export class EffectSprite {
  private sprite: Phaser.GameObjects.Sprite;
  private currentAnimation: Animation = DEFAULT_ANIMATION;
  private depthMap: { [part: string]: number } = {};

  constructor(
    parent: Phaser.GameObjects.Container,
    scene: Phaser.Scene,
    animation: Animation,
    isFacingLeft: boolean,
    depthMap: { [part: string]: number } = {},
  ) {
    this.currentAnimation = animation;
    this.depthMap = depthMap;
    const effectAsset = AssetLocator.getEffectAsset(animation);
    if (!effectAsset) {
      throw new Error(`No effect asset found for animation: ${animation}`);
    }

    this.sprite = scene.add.sprite(0, 0, effectAsset.key);

    // Visual centering offset to match cat sprites
    const VISUAL_CENTER_OFFSET = 137.5;

    // Depth & position depend on effect type
    const effectType = ANIMATION_EFFECTS[animation];
    switch (effectType) {
      case "umbrella":
        this.sprite.setDepth((depthMap["body"] ?? 0) - 1);
        this.sprite.setPosition(0, VISUAL_CENTER_OFFSET);
        break;
      case "stunned":
        this.sprite.setDepth((depthMap["hand_f"] ?? 0) + 1);
        this.sprite.setPosition(0, VISUAL_CENTER_OFFSET);
        break;
      case "throwing":
        this.sprite.setDepth((depthMap["hand_f"] ?? 0) + 1);
        this.sprite.setPosition(isFacingLeft ? -80 : 80, VISUAL_CENTER_OFFSET);
        break;
      case "hammer":
      default:
        this.sprite.setDepth((depthMap["hand_f"] ?? 0) + 1);
        this.sprite.setPosition(0, VISUAL_CENTER_OFFSET);
        break;
    }

    this.sprite.setFlipX(isFacingLeft);
    parent.add(this.sprite);

    // Ensure the animation exists, then play
    if (scene.textures.exists(effectAsset.key)) {
      if (!scene.anims.exists(effectAsset.key)) {
        const frames = scene.anims.generateFrameNames(effectAsset.key);
        scene.anims.create({
          key: effectAsset.key,
          frames,
          frameRate: 24,
          repeat: -1,
        });
      }
      this.sprite.play({ key: effectAsset.key, repeat: -1 });
    }
  }

  setFlipX(val: boolean) {
    this.sprite.setFlipX(val);
  }

  /** Update effect when cat animation changes */
  swap(animation: Animation, isFacingLeft: boolean) {
    if (animation === this.currentAnimation) return;

    // Destroy current sprite and recreate – simpler given small size
    const parent = this.sprite.parentContainer as Phaser.GameObjects.Container;
    const scene = this.sprite.scene;
    this.destroy();
    const replacement = new EffectSprite(
      parent,
      scene,
      animation,
      isFacingLeft,
      this.depthMap,
    );
    // Copy reference onto this
    this.sprite = (replacement as any).sprite;
    this.currentAnimation = animation;
  }

  destroy() {
    const parent = this.sprite.parentContainer as Phaser.GameObjects.Container;
    if (parent) parent.remove(this.sprite);
    this.sprite.destroy();
  }
}
